import React, { useContext, useEffect, useState } from 'react';
import {
  Button, Tooltip, TableCell, TableRow, TableContainer, Table, TableBody, TableHead,
  DialogActions, Box, FormControlLabel, Checkbox, DialogContent, Dialog, DialogTitle,
  IconButton, OutlinedInput,
} from '@mui/material';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { GetItemsResponse, Item } from '../../../../app/services/api/generated';
import { SearchSm } from '../../../../assets';
import useLogError from '../../../../hooks/useLogError';
import { StockCountZonesContext, StockCountZonesContextValues } from '../../contexts/StockCountZonesContext';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import CustomPagination from '../../../../components/forms/Pagination';

const StockCountZonesCreateItemsModal = () => {
  const {
    t,
    isItemsModalOpen,
    itemsData,
    handleClose,
    handleItems,
    firstSelectedStore,
    zoneName,
  } = useContext(StockCountZonesContext) as StockCountZonesContextValues;

  const { getItems } = useNswagClient();
  const { logError } = useLogError();

  const [apiData, setApiData] = useState<GetItemsResponse | undefined>();
  const [selectedItems, setSelectedItems] = useState<Item[]>([]);
  const [removedItemNumbers, setRemovedItemNumbers] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [isContinueSelecting, setIsContinueSelecting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastStoreNumber, setLastStoreNumber] = useState<string | undefined>();
  const [page, setPage] = useState(0);
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);

  const rowsPerPage = 25;

  useEffect(() => {
    if (firstSelectedStore?.storeNumber && firstSelectedStore.storeNumber !== lastStoreNumber) {
      setSearch('');
      setLastStoreNumber(firstSelectedStore.storeNumber);
      setPage(0);
      setShowUnassignedOnly(false);
      fetchItems('', 0, false);
    }
  }, [firstSelectedStore?.storeNumber]);

  useEffect(() => {
    if (firstSelectedStore?.storeNumber) {
      fetchItems(search, page, showUnassignedOnly);
    }
  }, [page]);

  useEffect(() => {
    if (isItemsModalOpen && firstSelectedStore?.storeNumber) {
      fetchItems(search, page, showUnassignedOnly);
    }
  }, [isItemsModalOpen]);

  const fetchItems = (searchTerm: string, pageNum: number, notInZone: boolean) => {
    const storeNumber = firstSelectedStore?.storeNumber ?? '';
    if (!storeNumber) {
      setApiData(undefined);
      return;
    }
    setIsLoading(true);
    const skip = pageNum * rowsPerPage;
    getItems(
      searchTerm,
      storeNumber,
      undefined,
      undefined,
      true,
      true,
      undefined,
      undefined,
      undefined,
      undefined,
      'description',
      'asc',
      skip,
      rowsPerPage,
      undefined,
      undefined,
      notInZone || undefined,
      true,
    )
      .then((result) => {
        if (result?.data) {
          setApiData(result.data);
        } else {
          logError(result);
        }
      })
      .catch((error) => logError(error))
      .finally(() => setIsLoading(false));
  };

  const handleSearch = () => {
    setPage(0);
    fetchItems(search, 0, showUnassignedOnly);
  };

  const items = apiData?.items ?? [];
  const totalPages = Math.ceil((apiData?.itemCount ?? 0) / rowsPerPage);

  const displayedItems = items;

  const handleItemChange = (item: Item, isChecked: boolean) => {
    const itemNumber = item.itemNumber ?? '';
    const isAlreadyInZone = itemsData.some(x => x.itemNumber === itemNumber);
    if (isChecked) {
      setRemovedItemNumbers(prev => prev.filter(n => n !== itemNumber));
      if (!isAlreadyInZone) {
        setSelectedItems(prev =>
          prev.some(e => e.itemNumber === itemNumber) ? prev : [...prev, item],
        );
      }
    } else if (isAlreadyInZone) {
      setRemovedItemNumbers(prev => (prev.includes(itemNumber) ? prev : [...prev, itemNumber]));
    } else {
      setSelectedItems(prev => prev.filter(e => e.itemNumber !== itemNumber));
    }
  };

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const pageItemNumbers = displayedItems.map(i => i.itemNumber ?? '');
    if (event.target.checked) {
      setRemovedItemNumbers(prev => prev.filter(n => !pageItemNumbers.includes(n)));
      setSelectedItems(prev => {
        const additions = displayedItems.filter(
          i =>
            !itemsData.some(x => x.itemNumber === i.itemNumber) &&
            !prev.some(e => e.itemNumber === i.itemNumber),
        );
        return [...prev, ...additions];
      });
    } else {
      const existingOnPage = displayedItems
        .filter(i => itemsData.some(x => x.itemNumber === i.itemNumber))
        .map(i => i.itemNumber ?? '');
      setRemovedItemNumbers(prev => Array.from(new Set([...prev, ...existingOnPage])));
      setSelectedItems(prev => prev.filter(e => !pageItemNumbers.includes(e.itemNumber ?? '')));
    }
  };

  const handleConfirm = () => {
    handleItems(selectedItems, removedItemNumbers);
    setSelectedItems([]);
    setRemovedItemNumbers([]);
    if (!isContinueSelecting) close();
  };

  const close = () => {
    setSelectedItems([]);
    setRemovedItemNumbers([]);
    handleClose();
  };

  const isItemSelected = (itemNumber: string | undefined) =>
    selectedItems.some((item) => item.itemNumber === itemNumber) ||
    (itemsData.some((item) => item.itemNumber === itemNumber) &&
      !removedItemNumbers.includes(itemNumber ?? ''));

  return (
    <Dialog open={isItemsModalOpen}
      onClose={close}
      maxWidth="md"
      fullWidth>
      <DialogTitle>
        {t('messages.itemsLookup')}
        <IconButton
          aria-label={t('modals.close')}
          onClick={close}
          sx={{ position: 'absolute', right: 16, top: 8, color: (theme) => theme.palette.custom.gray[800] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <OutlinedInput
              size="small"
              sx={{ maxWidth: '60%', height: '40px' }}
              fullWidth
              placeholder={t('modals.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button size="lg"
              aria-label={t('modals.search')}
              startIcon={<SearchSm />}
              onClick={handleSearch}
              sx={{ height: '40px' }} />
            <FormControlLabel
              control={
                <Checkbox
                  checked={showUnassignedOnly}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setShowUnassignedOnly(checked);
                    setPage(0);
                    fetchItems(search, 0, checked);
                  }}
                  size="small"
                />
              }
              label={t('modals.unassigned')}
              sx={{ m: 0, whiteSpace: 'nowrap' }}
            />
          </Box>
        </Box>

        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 400, mt: 2 }}>
          <Table size="small"
            stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox"
                  sx={{ width: 48 }}>
                  <Checkbox
                    color="primary"
                    indeterminate={displayedItems.some(i => isItemSelected(i.itemNumber)) && !displayedItems.every(i => isItemSelected(i.itemNumber))}
                    checked={!!displayedItems.length && displayedItems.every(i => isItemSelected(i.itemNumber))}
                    onChange={handleSelectAllChange}
                  />
                </TableCell>
                <TableCell>{t('items.description')}</TableCell>
                <TableCell align="right"
                  sx={{ width: 120 }}>{t('items.zone')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell align="center"
                    colSpan={3}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : (
                displayedItems.map((row) => {
                  const itemChecked = isItemSelected(row.itemNumber);
                  const isBeingRemoved = removedItemNumbers.includes(row.itemNumber ?? '');
                  const savedInCurrentZone = (row.zoneNames?.includes(zoneName) ?? false) && !isBeingRemoved;
                  const isPendingChange = itemChecked && !savedInCurrentZone;
                  return (
                    <TableRow key={row.id}>
                      <TableCell padding="checkbox"
                        sx={{ width: 48 }}>
                        <Checkbox
                          color="primary"
                          checked={itemChecked}
                          onChange={(e) => handleItemChange(row, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <strong>{row.itemNumber}</strong>
                          <br />
                          {row.description}
                        </Box>
                      </TableCell>
                      <TableCell align="right"
                        sx={{ width: 150 }}>
                        {((row.zoneNames && row.zoneNames.length > 0) || savedInCurrentZone || isPendingChange) && (
                          <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
                            {savedInCurrentZone && (
                              <Tooltip title={t('items.currentZone')}
                                arrow>
                                <Box component="span"
                                  sx={{ border: '1px solid #4caf50', color: '#4caf50', borderRadius: '16px', px: '8px', py: '2px', fontSize: '0.8125rem', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
                                  {zoneName?.trim() ? zoneName : t('items.unnamedZone')}
                                </Box>
                              </Tooltip>
                            )}
                            {isPendingChange && (
                              <Tooltip title={t('items.pendingChangeTooltip')}
                                arrow>
                                <Box component="span"
                                  sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', border: '1px solid #ed6c02', color: '#ed6c02', borderRadius: '16px', px: '8px', py: '2px', fontSize: '0.8125rem', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
                                  <Box component="span"
                                    sx={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#ed6c02' }} />
                                  {zoneName?.trim() ? zoneName : t('items.unnamedZone')}
                                </Box>
                              </Tooltip>
                            )}
                            {(() => {
                              const otherZones = (row.zoneNames ?? []).filter(z => z !== zoneName);
                              if (otherZones.length === 0) return null;
                              if (otherZones.length === 1) {
                                return (
                                  <Tooltip title={otherZones[0]}
                                    arrow>
                                    <Box component="span"
                                      sx={{ border: '1px solid', borderColor: 'primary.main', color: 'primary.main', borderRadius: '16px', px: '8px', py: '2px', fontSize: '0.8125rem', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
                                      {otherZones[0]}
                                    </Box>
                                  </Tooltip>
                                );
                              }
                              return (
                                <Tooltip title={otherZones.join(', ')}
                                  arrow>
                                  <Box component="span"
                                    sx={{ border: '1px solid', borderColor: 'primary.main', color: 'primary.main', borderRadius: '16px', px: '8px', py: '2px', fontSize: '0.8125rem', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
                                    {`+${otherZones.length} ${t('modals.zones')}`}
                                  </Box>
                                </Tooltip>
                              );
                            })()}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <CustomPagination
          page={page}
          setPage={setPage}
          maxPages={totalPages}
          breakpointForChangeDisplay={120000}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <FormControlLabel
            control={<Checkbox checked={isContinueSelecting}
              onChange={() => setIsContinueSelecting(!isContinueSelecting)}
              size="small" />}
            label={t('modals.continueSearching')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="secondary"
          size="lg"
          onClick={close}>
          {t('modals.cancel')}
        </Button>
        <Button size="lg"
          onClick={handleConfirm}>
          {t('modals.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockCountZonesCreateItemsModal;